<script>
  import { onMount } from "svelte";
  import DotIndicator from "../components/DotIndicator.svelte";
  import Header from "../components/Header.svelte";
  import RecommendationCard from "../components/recommendation/RecommendationCard.svelte";
  import { fetchRecommendations } from "../components/recommendation/RecommendationData.js";

  let recommendations = [];
  let activeIndex = 0;
  let scrollContainer;
  let innerContainer;
  let touchStartX = 0;
  let touchEndX = 0;
  let swipeDirection = 0; // -1 for left, 0 for none, 1 for right

  // Mobile detection
  let isMobile = false;

  if (typeof window !== "undefined") {
    isMobile = window.innerWidth < 768;
    window.addEventListener("resize", () => {
      isMobile = window.innerWidth < 768;
    });
  }

  function updateActiveIndex() {
    if (!scrollContainer || !recommendations.length) return;

    const scrollPosition = scrollContainer.scrollLeft;
    const containerWidth = scrollContainer.clientWidth;
    const maxScroll = scrollContainer.scrollWidth - containerWidth;

    // Calculate progress as a percentage (0 to 1)
    const progress = scrollPosition / maxScroll;
    // Convert to index based on total number of recommendations
    activeIndex = Math.min(
      Math.round(progress * (recommendations.length - 1)),
      recommendations.length - 1
    );
  }

  function handleDotClick(index) {
    if (!scrollContainer || !innerContainer) return;

    const cardWidth = innerContainer.children[0]?.offsetWidth || 0;
    const spacing = 40; // mr-10 = 40px
    const scrollTo = index * (cardWidth + spacing);

    scrollContainer.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });
  }

  // Mobile touch handling
  function handleTouchStart(e) {
    touchStartX = e.touches[0].clientX;
    swipeDirection = 0;
  }

  function handleTouchMove(e) {
    touchEndX = e.touches[0].clientX;
    const diff = touchEndX - touchStartX;
    const threshold = 20; // Small threshold to start showing visual feedback

    if (Math.abs(diff) > threshold) {
      swipeDirection = diff > 0 ? -1 : 1; // -1 for prev, 1 for next
    } else {
      swipeDirection = 0;
    }
  }

  function handleTouchEnd() {
    const swipeThreshold = 50; // Minimum distance for swipe detection
    const swipeDistance = touchStartX - touchEndX;

    if (
      swipeDistance > swipeThreshold &&
      activeIndex < recommendations.length - 1
    ) {
      // Swiped left - next recommendation
      handleDotClick(activeIndex + 1);
    } else if (swipeDistance < -swipeThreshold && activeIndex > 0) {
      // Swiped right - previous recommendation
      handleDotClick(activeIndex - 1);
    }

    // Reset swipe direction
    swipeDirection = 0;
  }

  onMount(() => {
    fetchRecommendations().then((data) => (recommendations = data));
  });
</script>

<section id="recommendations" class="pt-10 md:pt-20">
  <!-- Remove Background Patterns -->
  <Header title="Recommendations" subtitle="What People Say" />

  <section class="w-screen overflow-scroll flex flex-col">
    <div
      bind:this={scrollContainer}
      on:scroll={updateActiveIndex}
      on:touchstart={handleTouchStart}
      on:touchmove={handleTouchMove}
      on:touchend={handleTouchEnd}
      class="flex overflow-x-auto scroll-smooth no-scrollbar pb-4 flex-row swipe-container"
      class:swipe-left={swipeDirection === 1}
      class:swipe-right={swipeDirection === -1}
    >
      <div class="flex-none w-[30px] md:w-[100px] opacity-0"></div>
      <div bind:this={innerContainer} class="flex flex-row">
        {#each recommendations as rec, i}
          <RecommendationCard {rec} isLast={i === recommendations.length - 1} />
        {/each}
      </div>
      <div class="flex-none w-[30px] md:w-[100px] opacity-0"></div>
    </div>

    <!-- Mobile swipe instruction -->
    <div
      class="md:hidden text-center text-xs text-gray-500 dark:text-gray-400 mt-2"
    >
      <p class="swipe-instruction">
        <span>← Swipe to navigate →</span>
      </p>
    </div>

    <DotIndicator
      total={recommendations.length}
      current={activeIndex}
      onDotClick={handleDotClick}
      className="mb-8 md:mb-16"
    />
  </section>
</section>

<style>
  /* Hide scrollbar but keep functionality */
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }

  /* Custom scrollbar for recommendation text */
  div::-webkit-scrollbar {
    width: 4px;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .animate-shimmer {
    animation: shimmer 2s ease-in-out infinite;
  }

  /* Mobile swipe feedback */
  @media (max-width: 768px) {
    .swipe-container {
      transition: transform 0.2s ease-out;
      touch-action: pan-y; /* Allow vertical scrolling but capture horizontal swipes */
    }

    .swipe-left {
      transform: translateX(-15px); /* Visual feedback when swiping left */
    }

    .swipe-right {
      transform: translateX(15px); /* Visual feedback when swiping right */
    }

    .swipe-instruction {
      padding: 0.5rem;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 0.7;
      }
      50% {
        opacity: 0.3;
      }
    }
  }
</style>
